import { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, query, orderBy, limit, startAfter, DocumentSnapshot, getCountFromServer } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { WorshipReport } from '../../types/worshipReport';
import { Plus, Users, ChevronLeft, ChevronRight, X, Search, BarChart2, TrendingUp, CalendarCheck } from 'lucide-react';
import { ReportFilterBar, ReportFilters, defaultFilters, isFiltersActive, applyFilters } from '../ui/ReportFilterBar';
import { MeetingTypeSelect } from '../ui/MeetingTypeSelect';
import { useAuth } from '../../contexts/AuthContext';
import { WorshipReportCard } from './WorshipReportCard';
import { WorshipReportTable } from './WorshipReportTable';
import { WorshipReportPreview } from './WorshipReportPreview';
import { canEditReport, canDeleteReport } from '../../utils/permissions';
import WorshipReportForm from './WorshipReportForm';
import EditWorshipReportForm from './EditWorshipReportForm';
import { useConfirm } from '../../contexts/ConfirmContext';
import WorshipTrendChart from './WorshipTrendChart';

export default function WorshipReportsList() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [reports, setReports] = useState<WorshipReport[]>([]);
  const [allReports, setAllReports] = useState<WorshipReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters());
  const [showForm, setShowForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState<WorshipReport | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editingReport, setEditingReport] = useState<WorshipReport | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [firstDoc, setFirstDoc] = useState<DocumentSnapshot | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [totalReportsCount, setTotalReportsCount] = useState(0);
  const [pageStartCursors, setPageStartCursors] = useState<Map<number, DocumentSnapshot>>(new Map());
  const reportsPerPage = 10;
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [mobileQuickFilter, setMobileQuickFilter] = useState('all');

  const loadReports = async (page = 1, direction: 'next' | 'prev' | 'direct' = 'direct', cursor?: DocumentSnapshot | null) => {
    try {
      setLoading(true);
      const reportsRef = collection(db, 'worshipReports');
      
      // Charger le nombre total de rapports (seulement au premier chargement)
      if (page === 1 && direction === 'direct') {
        const countSnapshot = await getCountFromServer(query(reportsRef));
        setTotalReportsCount(countSnapshot.data().count);
        
        // Charger tous les rapports pour les statistiques et la recherche
        const allReportsQuery = query(reportsRef, orderBy('date', 'desc'));
        const allSnapshot = await getDocs(allReportsQuery);
        const allReportsData = allSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as WorshipReport[];
        setAllReports(allReportsData);
      }
      
      let q;
      
      if (direction === 'next' && cursor) {
        q = query(reportsRef, orderBy('date', 'desc'), startAfter(cursor), limit(reportsPerPage + 1));
      } else if (direction === 'prev' && cursor) {
        // Pour la page précédente, on utilise endBefore avec un ordre inversé
        q = query(reportsRef, orderBy('date', 'asc'), startAfter(cursor), limit(reportsPerPage + 1));
      } else if (direction === 'direct' && page > 1 && pageStartCursors.has(page)) {
        // Saut direct vers une page visitée
        const startCursor = pageStartCursors.get(page)!;
        q = query(reportsRef, orderBy('date', 'desc'), startAfter(startCursor), limit(reportsPerPage + 1));
      } else {
        // Page 1 ou chargement initial
        q = query(reportsRef, orderBy('date', 'desc'), limit(reportsPerPage + 1));
      }
      
      const snapshot = await getDocs(q);
      let reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as WorshipReport[];
      
      // Si on navigue vers la page précédente, inverser l'ordre
      if (direction === 'prev') {
        reportsData = reportsData.reverse();
      }
      
      // Vérifier s'il y a plus de rapports
      const hasMore = reportsData.length > reportsPerPage;
      if (hasMore) {
        reportsData.pop(); // Retirer le rapport supplémentaire utilisé pour la vérification
      }
      
      setReports(reportsData);
      setHasNextPage(hasMore);
      setHasPrevPage(page > 1);
      setCurrentPage(page);
      
      // Mettre à jour les curseurs de pagination
      if (reportsData.length > 0) {
        const newFirstDoc = snapshot.docs[0];
        const newLastDoc = snapshot.docs[reportsData.length - 1];
        
        setFirstDoc(newFirstDoc);
        setLastDoc(newLastDoc);
        
        // Sauvegarder le curseur de début de cette page
        if (!pageStartCursors.has(page)) {
          setPageStartCursors(prev => new Map(prev).set(page, newFirstDoc));
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement des rapports:', err);
      setError('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const getTotalPages = () => Math.ceil(totalReportsCount / reportsPerPage);

  const goToNextPage = () => {
    if (hasNextPage && lastDoc) {
      const nextPage = currentPage + 1;
      loadReports(nextPage, 'next', lastDoc);
    }
  };

  const goToPrevPage = () => {
    if (hasPrevPage && firstDoc) {
      const prevPage = currentPage - 1;
      loadReports(prevPage, 'prev', firstDoc);
    }
  };

  const goToPage = (pageNumber: number) => {
    if (pageNumber === 1) {
      setPageStartCursors(new Map()); // Reset cursors
      loadReports(1, 'direct');
    } else if (pageStartCursors.has(pageNumber)) {
      loadReports(pageNumber, 'direct');
    } else {
      // Pour les pages non visitées, on peut soit recharger depuis le début
      // soit implémenter une logique plus complexe
      console.warn(`Page ${pageNumber} non visitée, redirection vers page 1`);
      goToPage(1);
    }
  };
  const handleDelete = async (id: string) => {
    const ok = await confirm({ title: 'Supprimer le rapport', description: 'Cette action est irréversible. Êtes-vous sûr de vouloir supprimer ce rapport de culte ?', confirmLabel: 'Supprimer', cancelLabel: 'Annuler', variant: 'danger' });
    if (!ok) return;
    
    try {
      await deleteDoc(doc(db, 'worshipReports', id));
      // Recharger la page actuelle après suppression
      loadReports(currentPage, 'direct', currentPage > 1 ? pageStartCursors.get(currentPage) : null);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Erreur lors de la suppression du rapport');
    }
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    // Retourner à la page 1 après ajout d'un nouveau rapport
    setCurrentPage(1);
    setPageStartCursors(new Map());
    loadReports(1, 'direct');
  };

  const handlePreview = (report: WorshipReport) => {
    setSelectedReport(report);
    setShowPreview(true);
  };

  const handleEdit = (report: WorshipReport) => {
    setEditingReport(report);
  };

  const handleUpdateReport = () => {
    setEditingReport(null);
    // Recharger la page actuelle après modification
    loadReports(currentPage, 'direct', currentPage > 1 ? pageStartCursors.get(currentPage) : null);
  };

  const filtersActive = isFiltersActive(filters);
  const filteredReports = filtersActive
    ? applyFilters(
        allReports,
        filters,
        r => r.date,
        r => [
          new Date(r.date).toLocaleDateString('fr-FR'),
          r.messageTheme,
          r.speakerId,
          r.sourceChurch,
        ],
        r => r.meetingTypeId
      )
    : reports;

  const mobileBase = (mobileQuickFilter !== 'all' || mobileSearchQuery) ? allReports : filteredReports;
  const mobileFilteredReports = mobileBase.filter(r => {
    const d = new Date(r.date);
    const now = new Date();
    if (mobileQuickFilter === 'month' && (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear())) return false;
    if (mobileQuickFilter === 'sunday' && d.getDay() !== 0) return false;
    if (mobileQuickFilter === 'friday' && d.getDay() !== 5) return false;
    if (mobileSearchQuery && !r.messageTheme?.toLowerCase().includes(mobileSearchQuery.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#00665C]"></div>
      </div>
    );
  }

  // Utiliser tous les rapports pour les statistiques
  const statsReports = allReports.length > 0 ? allReports : reports;
  const totalPages = getTotalPages();

  return (

    <>
      {/* ===== MOBILE VIEW (< md) ===== */}
      <div className="md:hidden">
        <div className="min-h-screen bg-gray-50">
          {showForm && (
            <WorshipReportForm onSubmit={handleFormSubmit} onClose={() => setShowForm(false)} />
          )}
          {showPreview && selectedReport && (
            <WorshipReportPreview report={selectedReport} onClose={() => { setShowPreview(false); setSelectedReport(null); }} />
          )}
          {editingReport && (
            <EditWorshipReportForm report={editingReport} onClose={() => setEditingReport(null)} />
          )}
          {!showForm && !showPreview && !editingReport && (
            <>
              {/* Header */}
              <div className="bg-vh-green text-white px-4 pt-10 pb-6">
                <h1 className="text-2xl font-bold tracking-tight">Gestion de culte</h1>
                <p className="text-white/70 text-sm mt-1">{totalReportsCount} rapports au total</p>
              </div>

              {/* Nouveau rapport */}
              <div className="px-4 pt-4 pb-2">
                <button
                  onClick={() => setShowForm(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-vh-green text-white rounded-xl font-semibold text-sm shadow-md active:scale-95 transition-transform"
                >
                  <Plus className="w-4 h-4" />
                  Nouveau rapport
                </button>
              </div>

              {/* Stats 2x2 */}
              {statsReports.length > 0 && (
                <div className="px-4 py-3 grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-xl p-3 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Total rapports</p>
                    <p className="text-2xl font-bold text-vh-green">{totalReportsCount}</p>
                  </div>
                  <div className="bg-white rounded-xl p-3 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Participation moy.</p>
                    <p className="text-2xl font-bold text-vh-green">
                      {Math.round(statsReports.reduce((sum, r) => sum + r.totalParticipants, 0) / statsReports.length)}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-3 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Nvx membres moy.</p>
                    <p className="text-2xl font-bold text-vh-green">
                      {Math.round(statsReports.reduce((sum, r) => sum + r.totalNewMembers, 0) / statsReports.length)}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-3 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Dernier rapport</p>
                    <p className="text-xl font-bold text-vh-green">
                      {new Date(statsReports[0]?.date || '').toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              )}

              {/* Section header */}
              <div className="px-4 pt-1 pb-2 flex items-center justify-between">
                <span className="text-base font-semibold text-gray-800">Rapports récents</span>
                <span className="text-sm text-gray-500">{mobileFilteredReports.length}/{totalReportsCount}</span>
              </div>

              {/* Search + chips */}
              <div className="sticky top-0 z-10 bg-white shadow-sm">
                <div className="px-4 pt-2 pb-1 flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2 bg-gray-100 rounded-xl px-3 py-2">
                    <Search className="w-4 h-4 text-gray-400 shrink-0" />
                    <input
                      type="text"
                      placeholder="Rechercher un rapport..."
                      value={mobileSearchQuery}
                      onChange={e => setMobileSearchQuery(e.target.value)}
                      className="flex-1 bg-transparent text-sm outline-none text-gray-700 placeholder-gray-400"
                    />
                    {mobileSearchQuery && (
                      <button onClick={() => setMobileSearchQuery('')} className="text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="px-4 pb-2 flex gap-2 overflow-x-auto">
                  {['all', 'month', 'sunday', 'friday'].map(f => (
                    <button
                      key={f}
                      onClick={() => setMobileQuickFilter(f)}
                      className={'shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ' + (mobileQuickFilter === f ? 'bg-vh-green text-white' : 'bg-gray-100 text-gray-600')}
                    >
                      {f === 'all' ? 'Tous' : f === 'month' ? 'Ce mois' : f === 'sunday' ? 'Dimanche' : 'Vendredi'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Report list */}
              {loading && (
                <div className="flex justify-center items-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-vh-green" />
                </div>
              )}
              {error && (
                <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
              )}
              {!loading && (
                <div className="px-4 py-4 space-y-3">
                  {mobileFilteredReports.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <Users className="w-12 h-12 mb-3 text-gray-300" />
                      <p className="text-base font-medium">Aucun rapport trouvé</p>
                      <p className="text-sm mt-1">Appuyez sur + pour créer un rapport</p>
                    </div>
                  ) : (
                    mobileFilteredReports.map(report => (
                      <WorshipReportCard
                        key={report.id}
                        report={report}
                        onDelete={() => handleDelete(report.id)}
                        onPreview={handlePreview}
                        onEdit={handleEdit}
                        canEdit={canEditReport(report, user)}
                        canDelete={canDeleteReport(report, user)}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Pagination */}
              {mobileQuickFilter === 'all' && !mobileSearchQuery && !filtersActive && (hasPrevPage || hasNextPage) && (
                <div className="flex justify-between items-center px-4 pb-8 pt-2">
                  <button
                    onClick={goToPrevPage}
                    disabled={!hasPrevPage}
                    className={hasPrevPage ? 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-vh-green text-white' : 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed'}
                  >
                    <ChevronLeft className="w-4 h-4" /> Précédent
                  </button>
                  <span className="text-sm text-gray-500">Page {currentPage}/{totalPages}</span>
                  <button
                    onClick={goToNextPage}
                    disabled={!hasNextPage}
                    className={hasNextPage ? 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-vh-green text-white' : 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed'}
                  >
                    Suivant <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>


      {/* ===== DESKTOP VIEW (>= md) ===== */}
      <div className="hidden md:block">
        <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Users className="h-8 w-8 text-[#00665C]" />
              <h1 className="text-3xl font-bold text-gray-900">Gestion de Culte</h1>
            </div>
            <p className="text-gray-600">
              Gérez les rapports de présence et informations générales des cultes
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              showForm
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-[#00665C] text-white hover:bg-[#004d45]'
            }`}
          >
            {showForm ? (
              <><X className="h-4 w-4" />Fermer le formulaire</>
            ) : (
              <><Plus className="h-4 w-4" />Nouveau rapport</>
            )}
          </button>
        </div>

        {/* Statistiques rapides */}
        {statsReports.length > 0 && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-[#00665C]">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Rapports</h3>
                <BarChart2 className="h-4 w-4 text-[#00665C] opacity-70" />
              </div>
              <p className="text-2xl font-bold text-[#00665C]">{totalReportsCount}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Participation moy.</h3>
                <TrendingUp className="h-4 w-4 text-green-500 opacity-70" />
              </div>
              <p className="text-2xl font-bold text-green-600">
                {Math.round(statsReports.reduce((sum, r) => sum + r.totalParticipants, 0) / statsReports.length)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-400">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nvx membres moy.</h3>
                <Users className="h-4 w-4 text-blue-400 opacity-70" />
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {Math.round(statsReports.reduce((sum, r) => sum + r.totalNewMembers, 0) / statsReports.length)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-amber-400">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dernier rapport</h3>
                <CalendarCheck className="h-4 w-4 text-amber-400 opacity-70" />
              </div>
              <p className="text-lg font-bold text-gray-900">
                {new Date(statsReports[0]?.date || '').toLocaleDateString('fr-FR')}
              </p>
            </div>
          </div>
        )}

        {/* Actions et recherche */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div>
            <ReportFilterBar
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters(defaultFilters())}
              placeholder="Rechercher par thème, source, prédicateur…"
              resultCount={filteredReports.length}
              totalCount={allReports.length || totalReportsCount}
              meetingTypeSlot={
                <MeetingTypeSelect
                  value={filters.meetingTypeId ?? ''}
                  onChange={v => setFilters(f => ({ ...f, meetingTypeId: v }))}
                  required={false}
                />
              }
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Formulaire inline pour nouveau rapport */}
        {showForm && (
          <div className="mb-6">
            <WorshipReportForm
              onSubmit={handleFormSubmit}
              onClose={() => setShowForm(false)}
            />
          </div>
        )}
      {/* Graphique d'évolution du culte */}
      <WorshipTrendChart reports={allReports} />

        {/* Liste des rapports */}
        {filteredReports.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun rapport trouvé</h3>
            <p className="text-gray-600 mb-6">
              {filters.search ? 'Aucun rapport ne correspond à votre recherche.' : 'Commencez par créer votre premier rapport.'}
            </p>
            {!filters.search && (
              <button
                onClick={() => setShowForm(true)}
                className="bg-[#00665C] text-white px-4 py-2 rounded-lg hover:bg-[#004d45] transition-colors inline-flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Créer un rapport
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Version mobile - Cards */}
            <div className="sm:hidden space-y-4">
              {filteredReports.map((report) => (
                <WorshipReportCard
                  key={report.id}
                  report={report}
                  onPreview={handlePreview}
                  onEdit={handleEdit}
                  onDelete={() => handleDelete(report.id!)}
                  canEdit={canEditReport(report, user)}
                  canDelete={canDeleteReport(report, user)}
                />
              ))}
            </div>

            {/* Version desktop - Table */}
            <div className="hidden sm:block">
              <WorshipReportTable
                reports={filteredReports}
                onPreview={handlePreview}
                onEdit={handleEdit}
                onDelete={() => loadReports()}
              />
            </div>
          </>
        )}
      </div>
      
      {/* Pagination */}
      {!filtersActive && filteredReports.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm p-4 mt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Page {currentPage} sur {totalPages} • {totalReportsCount} rapports au total
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Bouton Précédent */}
              <button
                onClick={goToPrevPage}
                disabled={!hasPrevPage}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  hasPrevPage
                    ? 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                }`}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Précédent
              </button>
              
              {/* Numéros de page */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pageNumbers: (number | string)[] = [];
                  
                  // Toujours inclure la page 1
                  if (totalPages > 0) {
                    pageNumbers.push(1);
                  }
                  
                  // Ajouter des ellipses si nécessaire avant la page actuelle
                  if (currentPage > 4) {
                    pageNumbers.push('...');
                  }
                  
                  // Ajouter les pages autour de la page actuelle
                  const startPage = Math.max(2, currentPage - 1);
                  const endPage = Math.min(totalPages - 1, currentPage + 1);
                  
                  for (let i = startPage; i <= endPage; i++) {
                    if (!pageNumbers.includes(i)) {
                      pageNumbers.push(i);
                    }
                  }
                  
                  // Ajouter des ellipses si nécessaire après la page actuelle
                  if (currentPage < totalPages - 3) {
                    pageNumbers.push('...');
                  }
                  
                  // Toujours inclure la dernière page si elle n'est pas déjà incluse
                  if (totalPages > 1 && !pageNumbers.includes(totalPages)) {
                    pageNumbers.push(totalPages);
                  }
                  
                  return pageNumbers.map((pageNum, index) => {
                    if (pageNum === '...') {
                      return (
                        <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum as number)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          pageNum === currentPage
                            ? 'bg-[#00665C] text-white'
                            : 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  });
                })()}
              </div>
              
              {/* Bouton Suivant */}
              <button
                onClick={goToNextPage}
                disabled={!hasNextPage}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  hasNextPage
                    ? 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                }`}
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreview && selectedReport && (
        <WorshipReportPreview
          report={selectedReport}
          onClose={() => setShowPreview(false)}
        />
      )}

      {editingReport && (
        <EditWorshipReportForm
          report={editingReport}
          onClose={() => setEditingReport(null)}
          onUpdate={handleUpdateReport}
        />
      )}
    </div>
      </div>
    </>
  );
}
